import * as Sentry from '@sentry/react';

/**
 * The DSN to report to, or null. Production builds only: `vite` dev never
 * reports, even with VITE_SENTRY_DSN in a local .env, so local errors never
 * land in the prod project. The prod value reaches the bundle through the
 * VITE_SENTRY_DSN build arg in Dockerfile.web.
 */
export function sentryDsn(): string | null {
  if (!import.meta.env.PROD) return null;
  return import.meta.env.VITE_SENTRY_DSN || null;
}

/**
 * Initialize Sentry for frontend error tracking (see sentryDsn).
 */
export function initSentry(): void {
  const dsn = sentryDsn();
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: `globalbridge-web@${__APP_VERSION__}`,

    // Sample 10% of transactions for performance
    tracesSampleRate: 0.1,

    // Session replay on 50% of errors, mask all text/media
    replaysOnErrorSampleRate: 0.5,
    replaysSessionSampleRate: 0,

    // Never send PII
    sendDefaultPii: false,

    // Strip query params from breadcrumb URLs
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'navigation' && breadcrumb.data?.to) {
        try {
          const url = new URL(breadcrumb.data.to, window.location.origin);
          url.search = '';
          breadcrumb.data.to = url.pathname;
        } catch {
          // leave as-is if URL parsing fails
        }
      }
      return breadcrumb;
    },
  });
}

/**
 * Set Sentry user context (id only, no PII).
 */
export function setSentryUser(userId: string | null): void {
  if (userId) {
    Sentry.setUser({ id: userId });
  } else {
    Sentry.setUser(null);
  }
}

export { Sentry };
